import React, { useEffect, useState } from 'react'
import AdsNavbar from '../AdsNavbar'
import Loader from '../../../components/Loader'
import Calling from '../../../api/Calling';
import { toast } from 'react-toastify';
import generateCaptcha from '../../../components/captcha';
import { formatView } from '../../../components/formatView';
import FileUpload from '../../../components/ul/FileUpload';
import {
    imageSizeExceededMessage,
    MAX_FILE_SIZE_IMAGE,
} from '../../../components/imageType';
import { IoEyeSharp, IoTrash } from 'react-icons/io5';
import formatDate from '../../../components/formatDate';
import TopNavbar from '../TopNavbar';
import NotFound from '../../../components/ul/NotFound';

const OpenAds = () => {
    const [loader, setLoader] = useState(false);
    const [nativeAds, setNativeAds] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [editingGroupName, setEditingGroupName] = useState(null);
    const [editGroupTitle, setEditGroupTitle] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [showGroupForm, setShowGroupForm] = useState(false);
    const [showSwapForm, setShowSwapForm] = useState(false);
    const [newAd, setNewAd] = useState({ a_name: '', a_id: '', a_description: '', image: null });
    const [searchTerm, setSearchTerm] = useState('');
    const [group, setGroup] = useState('A');
    const [groups, setGroups] = useState([]);
    const [showGroupManagement, setShowGroupManagement] = useState(false);
    const [newGroup, setNewGroup] = useState({ groupName: '', cloneGroupName: '', groupTitle: '' });
    const [swapGroups, setSwapGroups] = useState({ fromGroup: '', toGroup: '' });
    const [editImage, setEditImage] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);

    // Available group names (A-K)
    const availableGroupNames = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K'];

    // Get next available group name
    const getNextAvailableGroupName = () => {
        const usedGroups = groups.map(g => g.a_group);
        return availableGroupNames.find(name => !usedGroups.includes(name)) || '';
    };

    // Fetch all groups
    const fetchGroups = async () => {
        try {
            const resp = await Calling.getOpenAdGroups();
            if (resp?.status) {
                if (resp.data && resp.data.length > 0) {
                    setGroups(resp.data);
                } else {
                    // Set default group when no groups available
                    setGroups([{ a_group: 'A', a_group_title: 'Normal', adsCount: 0 }]);
                }
                // Pre-set the next available group name
                setNewGroup(prev => ({
                    ...prev,
                    groupName: getNextAvailableGroupName()
                }));
            } else {
                toast.error(resp?.message);
                // Set default group on error
                setGroups([{ a_group: 'A', a_group_title: 'Normal', adsCount: 0 }]);
            }
        } catch (error) {
            console.error('Error fetching groups:', error);
            toast.error('Failed to fetch ad groups');
            // Set default group on error
            setGroups([{ a_group: 'A', a_group_title: 'Normal', adsCount: 0 }]);
        }
    };


    // Fetch ads for the selected group
    const fetchData = async () => {
        setLoader(true);
        try {
            const resp = await Calling.getOpenAds(group);
            if (resp?.status) {
                setNativeAds(resp?.data);
                setGroup(resp?.group || group);
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoader(false);
        }
    }

    useEffect(() => {
        fetchGroups();
        fetchData();
    }, []);

    useEffect(() => {
        fetchData();
    }, [group]);

    // Function to handle search input change
    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    }

    // Filtered nativeAds based on searchTerm
    const filteredAds = nativeAds.filter(ad =>
        ad.a_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ad.a_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ad.a_description?.toLowerCase().includes(searchTerm.toLowerCase() || '')
    );

    const handleEdit = (id) => {
        setEditingId(id);
        setEditImage(null);
        setPreviewImage(null);
    }

    // Handle image preview modal
    const handleImageClick = (imageUrl, adName) => {
        setSelectedImage({ url: imageUrl, name: adName });
        setShowImageModal(true);
    };

    const closeImageModal = () => {
        setShowImageModal(false);
        setSelectedImage(null);
    };

    // Handle image file change for add form
    const handleChangeFile = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > MAX_FILE_SIZE_IMAGE) {
                toast.error(imageSizeExceededMessage);
                e.target.value = '';
                return;
            }
            setNewAd({ ...newAd, image: file });
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewImage(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Handle image file change for edit form
    const handleEditImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > MAX_FILE_SIZE_IMAGE) {
                toast.error(imageSizeExceededMessage);
                e.target.value = '';
                return;
            }
            setEditImage(file);
        }
    };

    const handleSave = async (id) => {
        const adToUpdate = nativeAds.find(ad => ad._id === id);
        setLoader(true);

        try {
            const formData = new FormData();
            formData.append('a_id', adToUpdate.a_id);
            formData.append('a_description', adToUpdate?.a_description || '');

            // Only for Group A, allow image upload
            if (group === 'A' && editImage) {
                formData.append('image', editImage);
            }

            const resp = await Calling.updateOpenAds(id, formData);
            if (resp?.status) {
                toast.success(resp?.message);
                fetchData();
                setEditImage(null);
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to update ad');
        } finally {
            setLoader(false);
            setEditingId(null);
        }
    }

    const handleInputChange = (id, field, value) => {
        setNativeAds(nativeAds.map(item =>
            item._id === id ? { ...item, [field]: value } : item
        ));
    }

    const handleAddSubmit = async () => {
        if (newAd.a_name.trim() === '' || newAd.a_id.trim() === '' || newAd.a_description.trim() === '') {
            toast.error('Please fill all the fields');
            return;
        }

        setLoader(true);
        try {
            const selectedGroup = groups.find(g => g.a_group === group);
            const groupTitle = selectedGroup?.a_group_title || '';

            const formData = new FormData();
            formData.append('a_name', newAd.a_name.replace(/ /g, ''));
            formData.append('a_id', newAd.a_id.replace(/ /g, ''));
            formData.append('a_description', newAd.a_description);
            formData.append('a_group', group);
            formData.append('a_group_title', groupTitle);

            // Only for Group A, attach image
            if (group === 'A' && newAd.image) {
                formData.append('image', newAd.image);
            }

            const resp = await Calling.addOpenAds(formData);
            if (resp?.status) {
                toast.success(resp?.message);
                setNewAd({ a_name: '', a_id: '', a_description: '', image: null });
                setPreviewImage(null);
                setShowAddForm(false);
                fetchData();
                fetchGroups(); // Update group counts
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to add ad');
        } finally {
            setLoader(false);
        }
    }

    // Group management functions
    const handleCloneGroup = async () => {
        if (!newGroup.groupName.trim() || !newGroup.cloneGroupName.trim() || !newGroup.groupTitle.trim()) {
            toast.error('Please fill all required fields');
            return;
        }

        if (newGroup.groupName === newGroup.cloneGroupName) {
            toast.error('Target group name cannot be the same as source group');
            return;
        }

        // Validate that the group name is one of the allowed names (A-K)
        if (!availableGroupNames.includes(newGroup.groupName)) {
            toast.error('Group name must be a single letter from A to K');
            return;
        }

        setLoader(true);
        try {
            const body = {
                groupName: newGroup.groupName.trim(),
                cloneGroupName: newGroup.cloneGroupName.trim(),
                groupTitle: newGroup.groupTitle.trim()
            }
            const resp = await Calling.cloneOpenAdGroup(body);

            if (resp?.status) {
                toast.success(resp.message);
                setShowGroupForm(false);
                setNewGroup({
                    groupName: '',
                    cloneGroupName: '',
                    groupTitle: ''
                });
                fetchGroups();
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error('Error cloning group:', error);
            toast.error('Failed to clone group');
        } finally {
            setLoader(false);
        }
    };

    // Handle swap groups
    const handleSwapGroups = async () => {
        if (!swapGroups.fromGroup.trim() || !swapGroups.toGroup.trim()) {
            toast.error('Please select both groups to swap');
            return;
        }

        if (swapGroups.fromGroup === swapGroups.toGroup) {
            toast.error('Cannot swap a group with itself');
            return;
        }

        const content = `You are about to swap groups "${swapGroups.fromGroup}" and "${swapGroups.toGroup}". This will exchange all ads and settings between these groups. To confirm, please solve this math problem.`;
        const isCaptchaValid = generateCaptcha(content);

        if (!isCaptchaValid) {
            return toast.error('Captcha answer is not correct', { autoClose: 2000 });
        }

        setLoader(true);
        try {
            const body = {
                fromGroup: swapGroups.fromGroup.trim(),
                toGroup: swapGroups.toGroup.trim()
            };
            const resp = await Calling.openAdsGroupSwap(body);

            if (resp?.status) {
                toast.success(resp.message || 'Groups swapped successfully');
                setShowSwapForm(false);
                setSwapGroups({ fromGroup: '', toGroup: '' });
                fetchGroups();
                fetchData(); // Refresh current group data
            } else {
                toast.error(resp?.message || 'Failed to swap groups');
            }
        } catch (error) {
            console.error('Error swapping groups:', error);
            toast.error('Failed to swap groups');
        } finally {
            setLoader(false);
        }
    };

    // Handle edit group title
    const handleEditGroupTitle = (groupName, currentTitle) => {
        setEditingGroupName(groupName);
        setEditGroupTitle(currentTitle || '');
    };

    // Save updated group title
    const handleSaveGroupTitle = async () => {
        if (!editGroupTitle.trim()) {
            toast.error('Group title cannot be empty');
            return;
        }

        setLoader(true);
        try {
            const body = {
                groupName: editingGroupName,
                newTitle: editGroupTitle.trim()
            };

            const resp = await Calling.changeOpenAdGroupTitle(body);

            if (resp?.status) {
                toast.success(resp.message || 'Group title updated successfully');
                setEditingGroupName(null);
                setEditGroupTitle('');
                fetchGroups();
            } else {
                toast.error(resp?.message || 'Failed to update group title');
            }
        } catch (error) {
            console.error('Error updating group title:', error);
            toast.error('Failed to update group title');
        } finally {
            setLoader(false);
        }
    };

    // Cancel group title editing
    const handleCancelGroupTitleEdit = () => {
        setEditingGroupName(null);
        setEditGroupTitle('');
    };

    const handleDeleteGroup = async (groupName) => {
        if (groupName === 'A') {
            toast.error('Default Group (A) cannot be deleted');
            return;
        }

        const content = `You are about to delete the group "${groupName}" and all its ads. To confirm, please solve this math problem.`;
        const isCaptchaValid = generateCaptcha(content);

        if (!isCaptchaValid) {
            return toast.error('Captcha answer is not correct', { autoClose: 2000 });
        }

        setLoader(true);
        try {
            const resp = await Calling.deleteOpenAdGroup({ groupName });

            if (resp?.status) {
                toast.success(resp.message);
                fetchGroups();
                if (group === groupName) {
                    setGroup('A');
                }
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error('Error deleting group:', error);
            toast.error('Failed to delete group');
        } finally {
            setLoader(false);
        }
    };


    const deleteAdData = async (id) => {
        const content = `You are about to delete this ad. To confirm, please solve this math problem.`;
        const isCaptchaValid = generateCaptcha(content);

        if (!isCaptchaValid) {
            return toast.error('Captcha answer is not correct', { autoClose: 2000 });
        }
        setLoader(true);
        try {
            const response = await Calling.deleteOpenAds(id);
            if (response?.status) {
                toast.success(response?.message);
                fetchGroups(); // Update group counts
            } else {
                toast.error(response?.message);
            }
        } catch (error) {
            console.error('Error deleting ad:', error);
        } finally {
            setLoader(false);
            fetchData();
        }
    };

    // Toggle between ad view and group management view
    const toggleGroupManagement = () => {
        setShowGroupManagement(!showGroupManagement);
        // Reset forms when toggling
        setShowAddForm(false);
        setShowGroupForm(false);
        setShowSwapForm(false);
        setEditingGroupName(null);
        setEditGroupTitle('');
        setSwapGroups({ fromGroup: '', toGroup: '' });
    };

    // Format dropdown option text to include group name, title and count
    const formatGroupOption = (group) => {
        let title = group.a_group_title || (group.a_group === 'A' ? 'Default' : '');
        return `${group.a_group} (${title}) (${group.adsCount})`;
    };

    return (
        <>
            {loader ? (
                <Loader />
            ) : (
                <div className="w-full min-h-screen text-main-font">
                    {/* Page Header */}
                    <div className="mb-6">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-xl bg-blue-600 text-main-white">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                </svg>
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-main-font">Ad Management</h1>
                                <p className="text-sm text-main-subfont">Manage and configure your advertisements</p>
                            </div>
                        </div>
                    </div>

                    {/* Main Card */}
                    <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden">
                        <TopNavbar selectState={'android'} />
                        <hr className="border-main-border" />
                        <AdsNavbar selectState={'openads'} />
                        <hr className="border-main-border" />

                        {/* Header with toggle button */}
                        <div className="px-4 py-4">
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
                                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                                    <h2 className="text-lg font-semibold text-main-font">
                                        {showGroupManagement ? 'Open Ad Groups' : 'Open Ads'}
                                    </h2>
                                    {!showGroupManagement && (
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-main-subfont font-medium">Group:</span>
                                            <select
                                                value={group}
                                                onChange={(e) => setGroup(e.target.value)}
                                                className="px-4 py-2 border border-main-border rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none min-w-[250px] bg-main-white transition-all"
                                            >
                                                {groups.map((g, idx) => (
                                                    <option key={idx} value={g.a_group}>
                                                        {formatGroupOption(g)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>

                                {/* Action Buttons */}
                                <div className="flex flex-wrap gap-2">
                                    {showGroupManagement ? (
                                        <button
                                            onClick={toggleGroupManagement}
                                            className="px-4 py-2.5 text-sm text-main-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors flex items-center gap-2 font-medium"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                            View Ads
                                        </button>
                                    ) : (
                                        <>
                                            <button
                                                onClick={toggleGroupManagement}
                                                className="px-4 py-2.5 text-sm text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors flex items-center gap-2 font-medium"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                </svg>
                                                Manage Groups
                                            </button>
                                            <button
                                                onClick={() => setShowAddForm(!showAddForm)}
                                                className={`px-4 py-2.5 text-sm text-white rounded-xl transition-colors flex items-center gap-2 font-medium ${showAddForm ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-green-600 hover:bg-green-700'}`}
                                            >
                                                {showAddForm ? (
                                                    <>
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                        Cancel Add
                                                    </>
                                                ) : (
                                                    <>
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                        </svg>
                                                        Add Open Ads
                                                    </>
                                                )}
                                            </button>
                                        </>
                                    )}

                                    {/* Clone Group button in group management mode */}
                                    {showGroupManagement && (
                                        <button
                                            onClick={() => setShowGroupForm(!showGroupForm)}
                                            className={`px-4 py-2.5 text-sm text-white rounded-xl transition-colors flex items-center gap-2 font-medium ${showGroupForm ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-green-600 hover:bg-green-700'}`}
                                        >
                                            {showGroupForm ? (
                                                <>
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                    Cancel Clone
                                                </>
                                            ) : (
                                                <>
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                    </svg>
                                                    Clone Group
                                                </>
                                            )}
                                        </button>
                                    )}

                                    {/* Swap Group button in group management mode */}
                                    {showGroupManagement && (
                                        <button
                                            onClick={() => setShowSwapForm(!showSwapForm)}
                                            className={`px-4 py-2.5 text-sm text-white rounded-xl transition-colors flex items-center gap-2 font-medium ${showSwapForm ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-purple-600 hover:bg-purple-700'}`}
                                        >
                                            {showSwapForm ? (
                                                <>
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                    Cancel Swap
                                                </>
                                            ) : (
                                                <>
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                                    </svg>
                                                    Swap Groups
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Add Ad Form */}
                            {!showGroupManagement && showAddForm && (
                                <div className="p-4 rounded-xl space-y-4 bg-gray-50 border border-gray-200 mb-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                        <h3 className="text-base font-medium text-main-font">Add New Ad</h3>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="flex flex-col gap-2">
                                            <label className="text-sm text-main-subfont font-medium">Ad Name:</label>
                                            <input
                                                type="text"
                                                placeholder="Ad Name"
                                                value={newAd.a_name}
                                                onChange={(e) => setNewAd({ ...newAd, a_name: e.target.value })}
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none transition-all"
                                            />
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <label className="text-sm text-main-subfont font-medium">Ad ID:</label>
                                            <input
                                                type="text"
                                                placeholder="Ad ID"
                                                value={newAd.a_id}
                                                onChange={(e) => setNewAd({ ...newAd, a_id: e.target.value })}
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none transition-all"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex flex-col gap-2">
                                        <label className="text-sm text-main-subfont font-medium">Description:</label>
                                        <textarea
                                            placeholder="Ad Description"
                                            value={newAd?.a_description}
                                            onChange={(e) => setNewAd({ ...newAd, a_description: e.target.value })}
                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none transition-all"
                                            rows={3}
                                        />
                                    </div>

                                    {/* Image upload for Group A only */}
                                    {group === 'A' && (
                                        <div className="flex flex-col gap-2">
                                            <label className="text-sm text-main-subfont font-medium">Ad Image:</label>
                                            <FileUpload
                                                id="image"
                                                name='image'
                                                accept='image/png,image/jpg,image/jpeg,image/gif'
                                                onChange={handleChangeFile}
                                                required={false}
                                                className="relative z-10"
                                            />
                                            {previewImage && (
                                                <div className="mt-2">
                                                    <img
                                                        src={previewImage}
                                                        alt="Preview"
                                                        className="max-w-xs h-auto rounded-xl border border-gray-300"
                                                    />
                                                </div>
                                            )}
                                            <p className="text-xs text-main-subfont">
                                                Maximum file size: {MAX_FILE_SIZE_IMAGE / (1024 * 1024)}MB. Accepted formats: PNG, JPG, JPEG, GIF
                                            </p>
                                        </div>
                                    )}

                                    <div className="flex gap-3 pt-2">
                                        <button
                                            onClick={handleAddSubmit}
                                            className="px-4 py-2.5 text-sm text-main-white bg-main-blue rounded-xl hover:bg-main-blue/80 transition-colors flex items-center gap-2 font-medium"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                            Submit
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowAddForm(false);
                                                setNewAd({ a_name: '', a_id: '', a_description: '', image: null });
                                                setPreviewImage(null);
                                            }}
                                            className="px-4 py-2.5 text-sm text-main-delete border border-main-delete bg-main-white rounded-xl hover:bg-main-delete/10 transition-colors flex items-center gap-2 font-medium"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Clone Group Form */}
                            {showGroupManagement && showGroupForm && (
                                <div className="p-4 rounded-xl space-y-4 bg-gray-50 border border-gray-200 mb-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                        <h3 className="text-base font-medium text-main-font">Clone Existing Ad Group</h3>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="flex flex-col gap-2">
                                            <label className="text-sm text-main-subfont font-medium">
                                                New Group Name <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={newGroup.groupName}
                                                onChange={(e) => setNewGroup({ ...newGroup, groupName: e.target.value })}
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none bg-white transition-all"
                                            >
                                                <option value="">Select a group name</option>
                                                {availableGroupNames.map((name) => {
                                                    const isUsed = groups.some(g => g.a_group === name);
                                                    if (isUsed) return null;
                                                    return (
                                                        <option key={name} value={name}>{name}</option>
                                                    );
                                                }).filter(Boolean)}
                                            </select>
                                            <p className="text-xs text-main-subfont">Group names must be a single letter from A to K</p>
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <label className="text-sm text-main-subfont font-medium">
                                                Clone From <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={newGroup.cloneGroupName}
                                                onChange={(e) => setNewGroup({ ...newGroup, cloneGroupName: e.target.value })}
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none bg-white transition-all"
                                            >
                                                <option value="">Select a group</option>
                                                {groups.map((group, index) => (
                                                    <option key={index} value={group.a_group}>
                                                        {formatGroupOption(group)}
                                                    </option>
                                                ))}
                                            </select>
                                            <p className="text-xs text-main-subfont">All ads from this group will be copied to your new group</p>
                                        </div>
                                    </div>

                                    <div className="flex flex-col gap-2">
                                        <label className="text-sm text-main-subfont font-medium">
                                            Group Title/Description <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Enter a title or description for this group"
                                            value={newGroup.groupTitle}
                                            onChange={(e) => setNewGroup({ ...newGroup, groupTitle: e.target.value })}
                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none transition-all"
                                            required
                                        />
                                        <p className="text-xs text-main-subfont">A descriptive title to help identify the purpose of this group</p>
                                    </div>

                                    <div className="flex gap-3 pt-2">
                                        <button
                                            onClick={handleCloneGroup}
                                            className="px-4 py-2.5 text-sm text-main-white bg-main-blue rounded-xl hover:bg-main-blue/80 transition-colors flex items-center gap-2 font-medium"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                            </svg>
                                            Clone Group
                                        </button>
                                        <button
                                            onClick={() => setShowGroupForm(false)}
                                            className="px-4 py-2.5 text-sm text-main-delete border border-main-delete bg-main-white rounded-xl hover:bg-main-delete/10 transition-colors flex items-center gap-2 font-medium"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Swap Group Form */}
                            {showGroupManagement && showSwapForm && (
                                <div className="p-4 rounded-xl space-y-4 bg-gray-50 border border-gray-200 mb-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                        </svg>
                                        <h3 className="text-base font-medium text-main-font">Swap Two Ad Groups</h3>
                                    </div>

                                    <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-lg">
                                        <div className="flex">
                                            <div className="flex-shrink-0">
                                                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <div className="ml-3">
                                                <p className="text-sm text-yellow-700">
                                                    <strong>Warning:</strong> This will completely exchange all ads and settings between the two selected groups. This action cannot be undone automatically.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="flex flex-col gap-2">
                                            <label className="text-sm text-main-subfont font-medium">
                                                From Group <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={swapGroups.fromGroup}
                                                onChange={(e) => setSwapGroups({ ...swapGroups, fromGroup: e.target.value })}
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none bg-white transition-all"
                                            >
                                                <option value="">Select first group</option>
                                                {groups.map((group, index) => (
                                                    <option key={index} value={group.a_group}>
                                                        {formatGroupOption(group)}
                                                    </option>
                                                ))}
                                            </select>
                                            <p className="text-xs text-main-subfont">Select the first group to swap</p>
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <label className="text-sm text-main-subfont font-medium">
                                                To Group <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={swapGroups.toGroup}
                                                onChange={(e) => setSwapGroups({ ...swapGroups, toGroup: e.target.value })}
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none bg-white transition-all"
                                            >
                                                <option value="">Select second group</option>
                                                {groups.map((group, index) => (
                                                    <option key={index} value={group.a_group}>
                                                        {formatGroupOption(group)}
                                                    </option>
                                                ))}
                                            </select>
                                            <p className="text-xs text-main-subfont">Select the second group to swap</p>
                                        </div>
                                    </div>

                                    {swapGroups.fromGroup && swapGroups.toGroup && swapGroups.fromGroup !== swapGroups.toGroup && (
                                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
                                            <div className="flex">
                                                <div className="flex-shrink-0">
                                                    <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
                                                    </svg>
                                                </div>
                                                <div className="ml-3">
                                                    <p className="text-sm text-blue-700">
                                                        This will swap all ads between <strong>Group {swapGroups.fromGroup}</strong> and <strong>Group {swapGroups.toGroup}</strong>. The groups will exchange their complete content.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex gap-3 pt-2">
                                        <button
                                            onClick={handleSwapGroups}
                                            className="px-4 py-2.5 text-sm text-main-white bg-purple-600 rounded-xl hover:bg-purple-700 transition-colors flex items-center gap-2 font-medium"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                            </svg>
                                            Swap Groups
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowSwapForm(false);
                                                setSwapGroups({ fromGroup: '', toGroup: '' });
                                            }}
                                            className="px-4 py-2.5 text-sm text-main-delete border border-main-delete bg-main-white rounded-xl hover:bg-main-delete/10 transition-colors flex items-center gap-2 font-medium"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Search Bar - Only for Ad View */}
                        {!showGroupManagement && (
                            <div className="px-4 pb-4">
                                <div className="relative w-full lg:w-80">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-main-subfont" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    <input
                                        type="text"
                                        placeholder="Search Ads..."
                                        value={searchTerm}
                                        onChange={handleSearch}
                                        className="w-full pl-11 pr-4 py-2.5 border border-main-border rounded-xl bg-main-bg focus:bg-main-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none transition-all text-sm"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Group Management Help Text */}
                        {showGroupManagement && (
                            <div className="px-4 pb-4">
                                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <div className="ml-3">
                                            <p className="text-sm text-blue-700">
                                                Manage your Open Ad Groups here. Group A is the default group and cannot be deleted. All groups including Group A can have their titles edited.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Table */}
                        <div className="overflow-x-auto">
                            {/* Group Management Table */}
                            {showGroupManagement ? (
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-main-th">
                                            <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">No</th>
                                            <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Group Name</th>
                                            <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Title/Description</th>
                                            <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Ads Count</th>
                                            <th className="px-4 py-4 text-center text-xs font-semibold text-main-subfont uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-main-border">
                                        {groups.length > 0 ?
                                            groups.map((g, index) => (
                                                <tr key={index} className={`hover:bg-main-thHover transition-colors ${group === g.a_group ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}>
                                                    <td className="px-4 py-4 text-sm text-main-font">{index + 1}</td>
                                                    <td className="px-4 py-4 text-sm font-medium text-main-font">
                                                        {g.a_group}
                                                        {g.a_group === 'A' && (
                                                            <span className="ml-2 inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-green-50 text-green-600 border border-green-200">
                                                                Default
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-4 text-sm">
                                                        {editingGroupName === g.a_group ? (
                                                            <input
                                                                type="text"
                                                                value={editGroupTitle}
                                                                onChange={(e) => setEditGroupTitle(e.target.value)}
                                                                className="px-4 py-2 w-full border border-main-border rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-sm transition-all"
                                                                placeholder="Enter group title"
                                                                autoFocus
                                                            />
                                                        ) : (
                                                            <span className="text-sm text-main-font break-all">
                                                                {g.a_group_title || (g.a_group === 'A' ? 'Default' : 'N/A')}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-4 text-sm">
                                                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-600 border border-blue-200">
                                                            {g.adsCount} ads
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-4 text-center">
                                                        <div className="flex justify-center gap-2">
                                                            {editingGroupName === g.a_group ? (
                                                                <>
                                                                    <button
                                                                        onClick={handleSaveGroupTitle}
                                                                        className="px-3 py-1.5 text-xs text-main-white bg-green-500 hover:bg-green-600 rounded-lg transition-colors flex items-center gap-1 font-medium"
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                        </svg>
                                                                        Save
                                                                    </button>
                                                                    <button
                                                                        onClick={handleCancelGroupTitleEdit}
                                                                        className="px-3 py-1.5 text-xs text-main-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors flex items-center gap-1 font-medium"
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                        </svg>
                                                                        Cancel
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        onClick={() => {
                                                                            setGroup(g.a_group);
                                                                            setShowGroupManagement(false);
                                                                        }}
                                                                        className="px-3 py-1.5 text-xs text-main-white bg-main-blue hover:bg-main-blue/80 rounded-lg transition-colors flex items-center gap-1 font-medium"
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                                        </svg>
                                                                        View
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleEditGroupTitle(g.a_group, g.a_group_title)}
                                                                        className="px-3 py-1.5 text-xs text-main-white bg-yellow-500 hover:bg-yellow-600 rounded-lg transition-colors flex items-center gap-1 font-medium"
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                        </svg>
                                                                        Edit
                                                                    </button>
                                                                    {g.a_group !== 'A' && (
                                                                        <button
                                                                            onClick={() => handleDeleteGroup(g.a_group)}
                                                                            className="px-3 py-1.5 text-xs text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors flex items-center gap-1 font-medium"
                                                                        >
                                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                            </svg>
                                                                            Delete
                                                                        </button>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                            :
                                            <tr><td className="text-center py-12" colSpan={5}><NotFound /></td></tr>
                                        }
                                    </tbody>
                                </table>
                            ) : (
                                // Ad Management Table
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-main-th">
                                            <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">No</th>
                                            <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Ad Name</th>
                                            <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Ad ID</th>
                                            {group === 'A' && (
                                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Image</th>
                                            )}
                                            <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Description</th>
                                            {group === 'A' ?
                                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Updated At</th>
                                                :
                                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Call</th>
                                            }
                                            <th className="px-4 py-4 text-center text-xs font-semibold text-main-subfont uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-main-border">
                                        {filteredAds.length > 0 ?
                                            filteredAds?.map((item, index) => (
                                                <tr key={index} className="hover:bg-main-thHover transition-colors">
                                                    <td className="px-4 py-4 text-sm text-main-font">{index + 1}</td>
                                                    <td className="px-4 py-4 text-sm font-medium text-main-font">{item.a_name}</td>
                                                    <td className="px-4 py-4 text-sm">
                                                        {editingId === item._id ? (
                                                            <input
                                                                type="text"
                                                                value={item.a_id}
                                                                onChange={(e) => handleInputChange(item._id, 'a_id', e.target.value)}
                                                                className="px-4 py-2 w-full border border-main-border rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-sm transition-all"
                                                            />
                                                        ) : (
                                                            <span className="text-sm text-main-subfont break-all">{item.a_id}</span>
                                                        )}
                                                    </td>
                                                    {group === 'A' && (
                                                        <td className="px-4 py-4 text-sm">
                                                            {editingId === item._id ? (
                                                                <div className="flex flex-col gap-2">
                                                                    <FileUpload
                                                                        id={`edit-image-${item._id}`}
                                                                        name='image'
                                                                        accept='image/png,image/jpg,image/jpeg,image/gif'
                                                                        onChange={handleEditImageChange}
                                                                        required={false}
                                                                        className="relative z-10"
                                                                    />
                                                                    {item.a_img && !editImage && (
                                                                        <div className="relative group inline-block w-20 h-20">
                                                                            <img
                                                                                src={item.a_img}
                                                                                alt={item.a_name}
                                                                                className="w-20 h-20 object-cover rounded-xl border"
                                                                            />
                                                                            <div
                                                                                onClick={() => handleImageClick(item.a_img, item.a_name)}
                                                                                className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 rounded-xl flex items-center justify-center cursor-pointer transition-all duration-200"
                                                                            >
                                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                                                </svg>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <div className="flex items-center justify-center w-10">
                                                                    {item.a_img ? (
                                                                        <button
                                                                            onClick={() => handleImageClick(item.a_img, item.a_name)}
                                                                            className="inline-flex items-center justify-center w-9 h-9 bg-main-th text-main-blue border border-main-border hover:bg-main-thHover rounded-xl transition-colors"
                                                                        >
                                                                            <IoEyeSharp size={18} />
                                                                        </button>
                                                                    ) : (
                                                                        <span className="text-xs text-main-subfont">-</span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </td>
                                                    )}
                                                    <td className="px-4 py-4 text-sm">
                                                        {editingId === item._id ? (
                                                            <textarea
                                                                value={item.a_description}
                                                                onChange={(e) => handleInputChange(item._id, 'a_description', e.target.value)}
                                                                className="px-4 py-2 w-full border border-main-border rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-sm transition-all"
                                                            />
                                                        ) : (
                                                            <span className="text-sm text-main-subfont break-all">{item.a_description}</span>
                                                        )}
                                                    </td>
                                                    {group === 'A' ?
                                                        <td className="px-4 py-4 text-sm text-main-subfont">{formatDate(item.updatedAt)}</td>
                                                        :
                                                        <td className="px-4 py-4 text-sm text-main-subfont">{formatView(item.a_count)}</td>
                                                    }
                                                    <td className="px-4 py-4 text-center">
                                                        <div className="flex justify-center gap-2">
                                                            <button
                                                                onClick={() => editingId === item._id ? handleSave(item._id) : handleEdit(item._id)}
                                                                className={`px-3 py-1.5 text-xs text-main-white rounded-lg transition-colors font-medium ${editingId === item._id
                                                                    ? 'bg-green-500 hover:bg-green-600'
                                                                    : 'bg-main-blue hover:bg-main-blue/80'
                                                                    }`}
                                                            >
                                                                {editingId === item._id ? 'Save' : 'Edit'}
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    setEditingId(null);
                                                                    setEditImage(null);
                                                                    fetchData();
                                                                }}
                                                                className="px-3 py-1.5 text-xs font-medium text-main-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
                                                            >
                                                                Cancel
                                                            </button>
                                                            <button
                                                                onClick={() => deleteAdData(item._id)}
                                                                className="inline-flex items-center justify-center w-8 h-8 text-main-white bg-red-700 hover:bg-red-800 rounded-lg transition-colors"
                                                            >
                                                                <IoTrash className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                            :
                                            <tr><td className="text-center py-12" colSpan={group === 'A' ? 7 : 6}><NotFound /></td></tr>
                                        }
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Image Preview Modal */}
            {showImageModal && selectedImage && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
                    onClick={closeImageModal}
                >
                    <div className="relative max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden">
                        {/* Close button */}
                        <button
                            onClick={closeImageModal}
                            className="absolute top-4 right-4 z-10 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 transition-colors"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        {/* Image Header */}
                        <div className="bg-gray-50 px-6 py-4 border-b border-main-border">
                            <h3 className="text-lg font-semibold text-main-font">{selectedImage.name}</h3>
                        </div>

                        {/* Image Container */}
                        <div className="p-4 flex items-center justify-center bg-gray-50">
                            <img
                                src={selectedImage.url}
                                alt={selectedImage.name}
                                className="max-w-full max-h-[70vh] object-contain rounded-xl"
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default OpenAds